import React, { useMemo, useState } from 'react'
import { useTheme } from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { GridRow } from 'components/StaticGrid'
import DOMPurify from 'dompurify'
import {
  Wrapper,
  Header,
  LegacyActionsDropdownColumn,
  LegacyBackgroundLayer,
  LegacyContentColumn,
  ReleaseInfoFluidContainer,
  ScrollableContent
} from '../ReleaseInformation.styles'
import {
  PromoContent,
  MotionSidePanel
} from '../components/PromoDetails/PromoDetails.styles'
import PromoGlobalStyles from '../components/PromoDetails/PromoGlobalStyles'
import InfoTabs from '../components/Tabs/Tabs'
import {
  useMobileMediaQuery,
  useTabletAndAboveMediaQuery
} from 'utils/hooks/useMediaQueries'
import Breadcrumb from 'components/Breadcrumb/Breadcrumb'
import GlobalActionMenu from 'components/GlobalActionMenu/GlobalActionMenu'
import PromoDetails from '../components/PromoDetails/PromoDetails'
import DownloadSectionLegacy from '../components/DownloadSection/DownloadSectionLegacy'
import ActionsSectionLegacy from '../components/ActionsSection/ActionsSectionLegacy'
import ActionsDropdown from '../components/ActionsDropdown/ActionsDropdown'
import ReleaseDetailsLegacy from '../components/ReleaseDetails/ReleaseDetailsLegacy'
import HamburguerMenu from 'components/HamburguerMenu/HamburguerMenu'
import { Arrowback, Close } from '@dsny/dsny-component-library/icons'
import { Tonality } from '@dsny/dsny-component-library'
import ReleaseCoverArtModal from '../components/Modals/ReleaseCoverArtModal'
import { hasPromoEmailContent, PROMO_SANITIZE_OPTIONS } from '../utils'
import useReleaseInformationResponsiveBreakpointsV2 from '../hooks/useReleaseInformationResponsiveBreakpointsV2'

const ReleaseInformationLegacyLayout = ({
  release,
  setRelease,
  loading,
  handleDownload,
  layoutType = 'row'
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const isMobile = useMobileMediaQuery()
  const isTabletAndAbove = useTabletAndAboveMediaQuery()
  const responsiveBreakpointsV2 = useReleaseInformationResponsiveBreakpointsV2()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isPromoExpanded, setIsPromoExpanded] = useState(false)
  const showPromoSection = useMemo(
    () => hasPromoEmailContent(release?.promoinfo),
    [release?.promoinfo]
  )
  const sanitizedPromoinfoHtml =
    release?.promoinfo &&
    DOMPurify.sanitize(release?.promoinfo, PROMO_SANITIZE_OPTIONS)

  const generateLabels = (pathnames) => {
    const labels = []
    const mapping = {
      releases: 'Releases'
    }

    for (let index = 0; index < pathnames.length; index++) {
      const segment = pathnames[index]

      if (segment === 'releaseInfo') {
        continue
      }

      if (index === 2 && !isNaN(segment)) {
        labels.push({
          title: `Release Information - ${release?.title || ''}`,
          path: `releaseInfo/${segment}`
        })
        continue
      }

      labels.push({ title: mapping[segment] || segment, path: segment })
    }

    return labels
  }

  return (
    <Wrapper
      $responsiveBreakpointsV2={responsiveBreakpointsV2}
      data-cy="release-detail-root"
      data-layout={layoutType}
    >
      <Header
        $responsiveBreakpointsV2={responsiveBreakpointsV2}
        className={isPromoExpanded ? 'shifted' : ''}
      >
        <LegacyBackgroundLayer
          style={{ backgroundImage: `url(${release?.coverart})` }}
        />
        <ReleaseInfoFluidContainer>
          <GridRow
            style={{
              padding: '16px 28px 0 16px',
              flexDirection: 'row',
              alignItems: 'center',
              marginBottom: '24px'
            }}
          >
            <HamburguerMenu style={{ marginRight: 20 }} />
            {isTabletAndAbove && <Breadcrumb generateLabels={generateLabels} />}
            <GlobalActionMenu style={{ marginLeft: 'auto' }} />
          </GridRow>

          {isMobile && (
            <GridRow
              style={{
                padding: '16px',
                marginBottom: '12px'
              }}
            >
              <Arrowback
                width={18}
                style={{ marginRight: '16px' }}
                color={theme.colors.neutralW80}
                onClick={() => navigate('/releases')}
              />
              <Breadcrumb generateLabels={generateLabels} />
            </GridRow>
          )}

          <GridRow style={{ position: 'relative' }}>
            <ReleaseDetailsLegacy
              release={release}
              setIsModalOpen={setIsModalOpen}
              loading={loading}
            />
          </GridRow>
          <GridRow style={{ alignItems: 'center', marginBottom: '24px' }}>
            <DownloadSectionLegacy
              release={release}
              handleDownload={handleDownload}
              isMobile={isMobile}
              isPromoExpanded={isPromoExpanded}
            />

            {!isPromoExpanded && (
              <ActionsSectionLegacy release={release} setRelease={setRelease} />
            )}

            {isPromoExpanded && (
              <LegacyActionsDropdownColumn
                $responsiveBreakpointsV2={responsiveBreakpointsV2}
                style={{ justifyItems: 'flex-end' }}
              >
                <ActionsDropdown release={release} setRelease={setRelease} />
              </LegacyActionsDropdownColumn>
            )}
          </GridRow>
        </ReleaseInfoFluidContainer>
      </Header>

      <ScrollableContent
        $responsiveBreakpointsV2={responsiveBreakpointsV2}
        className={isPromoExpanded ? 'shifted' : ''}
      >
        <GridRow
          gutterWidth={24}
          style={{
            flex: 1,
            minHeight: 0,
            padding: '24px 24px 0',
            height: '100%'
          }}
          className="desktop-fixed-height"
        >
          <LegacyContentColumn
            $fullWidth={isPromoExpanded || !showPromoSection}
            $responsiveBreakpointsV2={responsiveBreakpointsV2}
            className="legacy-tabs-section"
            id="tabs-section"
          >
            <InfoTabs release={release} />
          </LegacyContentColumn>
          {showPromoSection && !isPromoExpanded && (
            <LegacyContentColumn
              $responsiveBreakpointsV2={responsiveBreakpointsV2}
              id="promo-section"
              style={{ overflow: 'hidden' }}
            >
              <PromoDetails
                promoInfo={release?.promoinfo}
                setIsExpanded={setIsPromoExpanded}
              />
            </LegacyContentColumn>
          )}
        </GridRow>
      </ScrollableContent>

      <AnimatePresence>
        {isPromoExpanded && (
          <MotionSidePanel
            $responsiveBreakpointsV2={responsiveBreakpointsV2}
            key="sidepanel"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
          >
            <div className="panel-header">
              <div>
                <Tonality.UIHeading
                  headingStyle="headline4"
                  color={theme.colors.neutralW90}
                  style={{ fontSize: 24 }}
                >
                  {release?.title}
                </Tonality.UIHeading>

                <Tonality.UIText
                  textStyle="body"
                  color={theme.colors.neutralW70}
                  style={{ fontSize: 14 }}
                >
                  {t('release-info.promotional-email')}
                </Tonality.UIText>
              </div>
              <Close
                width={24}
                height={24}
                color={theme.colors.neutralW80}
                onClick={() => setIsPromoExpanded(false)}
                style={{ cursor: 'pointer' }}
              />
            </div>
            <ScrollableContent
              $responsiveBreakpointsV2={responsiveBreakpointsV2}
            >
              <PromoGlobalStyles />
              <PromoContent id="promo-container" style={{ padding: '0' }}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitizedPromoinfoHtml
                  }}
                />
              </PromoContent>
            </ScrollableContent>
          </MotionSidePanel>
        )}
      </AnimatePresence>

      {isModalOpen && (
        <ReleaseCoverArtModal
          coverArt={release?.coverart}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </Wrapper>
  )
}

export default ReleaseInformationLegacyLayout
